import { useMemo } from 'react';

export interface DateRangeConfig {
  startDate?: Date;
  endDate?: Date;
}

export interface QuickDateRange {
  startDate: Date;
  endDate: Date;
}

export interface DateRangeResult {
  formatRange: (start?: Date, end?: Date) => string;
  formatCompact: (date?: Date) => string;
  getMonthStart: (date: Date) => Date;
  getMonthEnd: (date: Date) => Date;
  isValidRange: (start?: Date, end?: Date) => boolean;
  getQuickRanges: () => Record<string, QuickDateRange>;
}

export function useDateRange(): DateRangeResult {
  return useMemo(
    () => ({
      formatRange: (start?: Date, end?: Date): string => {
        if (!start && !end) return 'All time';
        if (start && !end) return `From ${formatShortMonthYear(start)}`;
        if (!start && end) return `Until ${formatShortMonthYear(end)}`;
        if (start && end) {
          if (isSameMonth(start, end)) {
            return formatShortMonthYear(start);
          }
          return `${formatShortMonthYear(start)} - ${formatShortMonthYear(end)}`;
        }
        return '';
      },

      formatCompact: (date?: Date): string => {
        if (!date) return 'Select';
        const monthShort = date.toLocaleDateString('en-US', { month: 'short' });
        const yearShort = date.getFullYear().toString().slice(-2);
        return `${monthShort} '${yearShort}`;
      },

      getMonthStart: (date: Date): Date => {
        return new Date(date.getFullYear(), date.getMonth(), 1);
      },

      getMonthEnd: (date: Date): Date => {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0);
      },

      isValidRange: (start?: Date, end?: Date): boolean => {
        if (!start || !end) return true;
        return start <= end;
      },

      getQuickRanges: (): Record<string, QuickDateRange> => {
        const now = new Date();

        return {
          lastThreeMonths: {
            startDate: new Date(now.getFullYear(), now.getMonth() - 2, 1),
            endDate: new Date(now.getFullYear(), now.getMonth() + 1, 0),
          },
          lastSixMonths: {
            startDate: new Date(now.getFullYear(), now.getMonth() - 5, 1),
            endDate: new Date(now.getFullYear(), now.getMonth() + 1, 0),
          },
          lastTwelveMonths: {
            startDate: new Date(now.getFullYear(), now.getMonth() - 11, 1),
            endDate: new Date(now.getFullYear(), now.getMonth() + 1, 0),
          },
          thisYear: {
            startDate: new Date(now.getFullYear(), 0, 1),
            endDate: new Date(now.getFullYear(), now.getMonth() + 1, 0),
          },
          lastYear: {
            startDate: new Date(now.getFullYear() - 1, 0, 1),
            endDate: new Date(now.getFullYear() - 1, 11, 31),
          },
        };
      },
    }),
    [],
  );
}

function formatShortMonthYear(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  });
}

function isSameMonth(date1: Date, date2: Date): boolean {
  return date1.getMonth() === date2.getMonth() && date1.getFullYear() === date2.getFullYear();
}
